// ============================================================================
// ActionModalHost
// Mounted once in LiveSession beside AvatarContributionsTab. Switches on
// pendingAction.action.type and opens the matching existing entity modal in
// create mode (prefilled from the avatar's draft) or, for mutate/resolve
// actions, on the existing entity resolved from `target`. A resolution miss
// (hallucinated id) falls through to the equivalent create modal with a
// visible warning, never a hard error.
//
// Self-fetches the small bits of session data it needs (personas, features,
// the one target story/RAID item) rather than requiring LiveSession to lift
// and thread full requirement lists down just for this rarely-open modal.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { X, FolderPlus, Lightbulb, History, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../services/api';
import { NFREditModal } from '../NFREditModal';
import { FeatureEditModal } from '../FeatureEditModal';
import { ADREditModal } from '../ADREditModal';
import { StoryEditModal } from '../StoryEditModal';
import { PersonaCreateModal } from '../PersonaCreateModal';
import { RaidEditModal } from '../raid/RaidEditModal';
import { TriggerTaskConfirmModal } from './TriggerTaskConfirmModal';
import { UserStory, Feature, NFR, ADR, getFeatures } from '../../../services/workshop';
import { listSessionRaidItems } from '../../../services/raid';
import { RaidItem } from '../../../types/raid';
import { PendingAvatarAction, useAvatarActions } from './useAvatarActions';
import { getAvatarTriggerableTask } from './avatarTriggerableTasks';

type UseAvatarActionsReturn = ReturnType<typeof useAvatarActions>;

interface ActionModalHostProps extends UseAvatarActionsReturn {
  pendingAction: PendingAvatarAction | null;
  sessionId?: string;
}

const EMPTY_STORY = (): UserStory => ({
  id: 'draft', persona: '', story: '', acceptanceCriteria: [], priority: 'medium', tags: [],
});
const EMPTY_NFR = (): NFR => ({
  id: 'draft', category: 'usability', title: '', description: '', priority: 'medium', acceptanceCriteria: [],
});
const EMPTY_ADR = (sessionId: string): ADR => ({
  id: 'draft', sessionId, title: '', status: 'proposed', context: '', decision: '', consequences: '',
});
const EMPTY_FEATURE = (sessionId: string): Feature => ({
  id: 'draft', sessionId, name: '', description: '',
});

// ============================================================================
// Small confirm-only modals for the two review action shapes that don't fit
// an existing entity edit modal — grouping is a multi-story tag-write, and
// note-append must never touch story/acceptanceCriteria (that's the whole
// point of not reusing reword_story for these).
// ============================================================================

function ConfirmModalShell({
  icon, title, subtitle, onClose, children, footer,
}: {
  icon: React.ReactNode; title: string; subtitle?: string; onClose: () => void;
  children: React.ReactNode; footer: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary flex-shrink-0">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-bold text-navy leading-tight">{title}</h2>
              {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-3">{children}</div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex-shrink-0">
          {footer}
        </div>
      </div>
    </div>
  );
}

function GroupStoriesConfirmModal({
  groupLabel, stories, saving, onConfirm, onClose,
}: {
  groupLabel: string; stories: UserStory[]; saving: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <ConfirmModalShell
      icon={<FolderPlus size={16} />}
      title="Group stories"
      subtitle="An avatar suggested grouping these related stories"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={saving} className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
            {saving ? 'Applying…' : 'Apply grouping'}
          </button>
        </>
      }
    >
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Group label</label>
        <p className="text-sm text-navy font-medium">{groupLabel}</p>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Stories ({stories.length})</label>
        <ul className="space-y-2">
          {stories.map(s => (
            <li key={s.id} className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2">{s.story}</li>
          ))}
        </ul>
      </div>
    </ConfirmModalShell>
  );
}

function StoryNoteConfirmModal({
  icon, title, story, noteText, saving, onConfirm, onClose,
}: {
  icon: React.ReactNode; title: string; story: UserStory; noteText: string;
  saving: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <ConfirmModalShell icon={icon} title={title} onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={saving || story.locked} className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60">
            {saving ? 'Adding…' : 'Add to story notes'}
          </button>
        </>
      }
    >
      {story.locked && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
          <Lock size={13} className="shrink-0" />
          This story is locked and cannot be edited until unlocked.
        </div>
      )}
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Story</label>
        <p className="text-sm text-navy leading-snug">{story.story}</p>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Avatar suggestion</label>
        <p className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 whitespace-pre-wrap">{noteText}</p>
      </div>
    </ConfirmModalShell>
  );
}

export function ActionModalHost({
  pendingAction, sessionId,
  close, applyCreatePersona, applyCreateUserStory, applyCreateNfr, applyCreateAdr, applyCreateFeature,
  applyCreateRaidItem, applyResolveRaidItem, applyStoryMutation, applyLinkMilestone, applyLinkRaid,
  applyTriggerTask, applyStoryNote, applyGroupStories,
}: ActionModalHostProps) {
  const [personas, setPersonas] = useState<{ id: string; name: string }[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [targetStory, setTargetStory] = useState<UserStory | null>(null);
  const [groupStories, setGroupStories] = useState<UserStory[]>([]);
  const [targetRaidItem, setTargetRaidItem] = useState<RaidItem | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [applyingAction, setApplyingAction] = useState(false);

  useEffect(() => {
    if (!pendingAction || !sessionId) return;
    const { action } = pendingAction;
    let cancelled = false;
    const draft = (action.draft || {}) as Record<string, any>;

    setLoadingContext(true);
    (async () => {
      try {
        const needsPersonas = ['create_user_story', 'create_feature', 'add_ac', 'reword_story', 'set_priority'].includes(action.type);
        const needsFeatures = ['create_user_story', 'create_nfr', 'create_adr'].includes(action.type);
        const needsStory = ['add_ac', 'reword_story', 'set_priority', 'suggest_alternative_approach', 'flag_legacy_assumption'].includes(action.type) && !!action.target?.entityId;
        const needsGroupStories = action.type === 'suggest_story_group' && Array.isArray(draft.storyIds) && draft.storyIds.length > 0;
        const needsRaidItem = ['resolve_raid_item', 'link_raid'].includes(action.type) && !!action.target?.entityId;

        const [personasRes, featuresRes, storiesRes, raidRes] = await Promise.all([
          needsPersonas ? api.get(`/api/sessions/${sessionId}/personas`) : Promise.resolve(null),
          needsFeatures ? getFeatures(sessionId) : Promise.resolve(null),
          (needsStory || needsGroupStories) ? api.get(`/api/sessions/${sessionId}/user-stories`) : Promise.resolve(null),
          needsRaidItem ? listSessionRaidItems(sessionId) : Promise.resolve(null),
        ]);
        if (cancelled) return;

        if (personasRes) setPersonas((personasRes.data.personas || []).map((p: any) => ({ id: p.id, name: p.name })));
        if (featuresRes) setFeatures(featuresRes);
        if (storiesRes) {
          const stories: UserStory[] = storiesRes.data.stories || storiesRes.data.userStories || [];
          if (needsStory) {
            const found = stories.find(s =>
              s.id === action.target?.entityId && (!action.target?.persona || s.persona === action.target?.persona)) || null;
            setTargetStory(found);
          }
          if (needsGroupStories) {
            const ids: string[] = draft.storyIds;
            setGroupStories(stories.filter(s => ids.includes(s.id)));
          }
        }
        if (raidRes) {
          const found = raidRes.raidItems.find(r => r.raidId === action.target?.entityId) || null;
          setTargetRaidItem(found);
        }
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    })();

    return () => { cancelled = true; };
  }, [pendingAction, sessionId]);

  if (!pendingAction) return null;
  const { action, contributionId } = pendingAction;
  const draft = (action.draft || {}) as Record<string, any>;

  const warnMissingTarget = () => {
    toast.warning(`"${action.label}" pointed at an item that no longer exists — opening a blank form instead.`);
  };

  if (loadingContext) return null;

  switch (action.type) {
    case 'create_persona':
      return (
        <PersonaCreateModal
          draft={draft}
          onSave={persona => applyCreatePersona(contributionId, action, persona)}
          onClose={close}
        />
      );

    case 'create_user_story':
      return (
        <StoryEditModal
          story={{ ...EMPTY_STORY(), ...draft }}
          personas={personas}
          features={features}
          sessionId={sessionId}
          createMode
          onSave={async (_id, patch) => applyCreateUserStory(contributionId, action, patch)}
          onClose={close}
        />
      );

    case 'create_nfr':
      return (
        <NFREditModal
          nfr={{ ...EMPTY_NFR(), ...draft }}
          features={features}
          sessionId={sessionId}
          createMode
          onSave={async (_id, patch) => applyCreateNfr(contributionId, action, patch)}
          onClose={close}
        />
      );

    case 'create_adr':
      return (
        <ADREditModal
          adr={{ ...EMPTY_ADR(sessionId || ''), ...draft }}
          features={features}
          sessionId={sessionId}
          createMode
          onSave={async (_id, patch) => applyCreateAdr(contributionId, action, patch)}
          onClose={close}
        />
      );

    case 'create_feature':
      return (
        <FeatureEditModal
          feature={{ ...EMPTY_FEATURE(sessionId || ''), ...draft }}
          personas={personas}
          createMode
          onSave={async (_id, patch) => applyCreateFeature(contributionId, action, patch)}
          onClose={close}
        />
      );

    case 'create_raid_item':
      return (
        <RaidEditModal
          item={null}
          defaults={{ type: (draft.type as any) || 'risk', links: draft.links }}
          candidates={[]}
          onSave={input => applyCreateRaidItem(contributionId, action, { ...input, ...draft })}
          onClose={close}
        />
      );

    case 'resolve_raid_item': {
      if (!targetRaidItem) {
        warnMissingTarget();
        return (
          <RaidEditModal
            item={null}
            candidates={[]}
            onSave={input => applyCreateRaidItem(contributionId, action, input)}
            onClose={close}
          />
        );
      }
      return (
        <RaidEditModal
          item={targetRaidItem}
          candidates={[]}
          onSave={() => Promise.resolve(close())}
          onResolve={(status, resolution) =>
            applyResolveRaidItem(contributionId, action, targetRaidItem.raidId, { status: status as any, resolution })}
          onClose={close}
        />
      );
    }

    case 'add_ac':
    case 'reword_story':
    case 'set_priority': {
      if (!targetStory) {
        warnMissingTarget();
        return (
          <StoryEditModal
            story={EMPTY_STORY()}
            personas={personas}
            features={features}
            sessionId={sessionId}
            createMode
            onSave={async (_id, patch) => applyCreateUserStory(contributionId, action, patch)}
            onClose={close}
          />
        );
      }
      const seeded: UserStory = { ...targetStory };
      if (action.type === 'reword_story' && typeof draft.story === 'string') seeded.story = draft.story;
      if (action.type === 'set_priority' && draft.priority) seeded.priority = draft.priority;
      if (action.type === 'add_ac' && Array.isArray(draft.acceptanceCriteria)) {
        seeded.acceptanceCriteria = [...targetStory.acceptanceCriteria, ...draft.acceptanceCriteria];
      }
      return (
        <StoryEditModal
          story={seeded}
          personas={personas}
          features={features}
          sessionId={sessionId}
          onSave={async (_id, patch) => applyStoryMutation(contributionId, action, targetStory.id, {
            story: patch.story, acceptanceCriteria: patch.acceptanceCriteria, priority: patch.priority,
          })}
          onClose={close}
        />
      );
    }

    case 'link_milestone': {
      const milestoneId = draft.milestoneId as string | undefined;
      if (!milestoneId) {
        warnMissingTarget();
      } else {
        applyLinkMilestone(contributionId, action, milestoneId, {
          storyIds: action.target?.entityType === 'user_story' && action.target.entityId ? [action.target.entityId] : undefined,
          featureIds: action.target?.entityType === 'feature' && action.target.entityId ? [action.target.entityId] : undefined,
        }).then(
          () => toast.success(`Linked to milestone`),
          () => toast.error(`Failed to link "${action.label}"`)
        );
      }
      close();
      return null;
    }

    case 'link_raid': {
      const raidId = action.target?.entityId;
      if (!raidId || !draft.link) {
        warnMissingTarget();
      } else {
        applyLinkRaid(contributionId, action, raidId, draft.link).then(
          () => toast.success(`Linked "${action.label}"`),
          () => toast.error(`Failed to link "${action.label}"`)
        );
      }
      close();
      return null;
    }

    case 'trigger_task': {
      const templateId = draft.templateId as string | undefined;
      const task = templateId ? getAvatarTriggerableTask(templateId) : undefined;
      if (!task) {
        toast.warning(`"${action.label}" referenced a task that isn't currently allowed — dismissing.`);
        close();
        return null;
      }
      return (
        <TriggerTaskConfirmModal
          action={action}
          task={task}
          instruction={typeof draft.instruction === 'string' ? draft.instruction : ''}
          onConfirm={instruction => applyTriggerTask(contributionId, action, templateId!, instruction)}
          onClose={close}
        />
      );
    }

    case 'suggest_story_group': {
      const groupLabel = typeof draft.groupLabel === 'string' ? draft.groupLabel : action.label;
      if (groupStories.length < 2) {
        warnMissingTarget();
        close();
        return null;
      }
      return (
        <GroupStoriesConfirmModal
          groupLabel={groupLabel}
          stories={groupStories}
          saving={applyingAction}
          onConfirm={async () => {
            setApplyingAction(true);
            try {
              await applyGroupStories(contributionId, action, groupStories.map(s => s.id), groupLabel);
              toast.success('Grouping applied');
              close();
            } catch {
              toast.error('Failed to apply grouping');
            } finally {
              setApplyingAction(false);
            }
          }}
          onClose={close}
        />
      );
    }

    case 'suggest_alternative_approach':
    case 'flag_legacy_assumption': {
      if (!targetStory) {
        warnMissingTarget();
        close();
        return null;
      }
      const noteText = action.type === 'suggest_alternative_approach'
        ? (typeof draft.alternativeText === 'string' ? draft.alternativeText : action.rationale || action.label)
        : [draft.assumption, draft.challenge].filter((s): s is string => typeof s === 'string' && !!s).join(' — ') || action.rationale || action.label;
      return (
        <StoryNoteConfirmModal
          icon={action.type === 'suggest_alternative_approach' ? <Lightbulb size={16} /> : <History size={16} />}
          title={action.type === 'suggest_alternative_approach' ? 'Alternative approach' : 'Challenge legacy assumption'}
          story={targetStory}
          noteText={noteText}
          saving={applyingAction}
          onConfirm={async () => {
            if (targetStory.locked) return;
            setApplyingAction(true);
            try {
              const mergedNotes = [...(targetStory.notes || []), noteText];
              await applyStoryNote(contributionId, action, targetStory.id, mergedNotes);
              toast.success('Added to story notes');
              close();
            } catch {
              toast.error('Failed to add note');
            } finally {
              setApplyingAction(false);
            }
          }}
          onClose={close}
        />
      );
    }

    default:
      return null;
  }
}
